import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Sparkles, Wand2, Compass, TrendingUp } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { HeroCarousel } from '@/components/HeroCarousel';
import { TutorialCard } from '@/components/TutorialCard';
import { useAppData } from '@/hooks/useAppData';
import { useUserActions } from '@/hooks/useUserActions';
import { Button } from '@/components/ui/button';

export default function Home() {
  const { tutorials, categories } = useAppData();
  const { favorites, toggleFavorite, addHistory } = useUserActions();

  const hotTutorials = tutorials.filter((t) => t.isHot).slice(0, 5);
  
  // Prepare data by category
  const categoryData = categories.map(cat => ({
    ...cat,
    items: tutorials.filter(t => t.categoryId === cat.id).slice(0, 4)
  }));

  return (
    <Layout>
      <div className="container mx-auto px-4 md:px-6">
        {/* Carousel */}
        {hotTutorials.length > 0 && (
          <section className="mt-4">
            <HeroCarousel tutorials={hotTutorials} />
          </section>
        )}

        {/* Quick Access Cards */}
        <section className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link
            to="/ai-creator"
            className="group relative overflow-hidden rounded-3xl border bg-gradient-to-br from-violet-500/10 to-fuchsia-500/10 p-6 hover:shadow-lg transition-all hover:border-primary/50"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-violet-500 text-white p-2 rounded-xl">
                <Wand2 className="h-5 w-5" />
              </div>
              <h3 className="font-bold text-lg">AI 创意生成</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              用 AI 生成封面、海报、配图
            </p>
          </Link>

          <Link
            to="/resources"
            className="group relative overflow-hidden rounded-3xl border bg-gradient-to-br from-blue-500/10 to-cyan-500/10 p-6 hover:shadow-lg transition-all hover:border-primary/50"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-blue-500 text-white p-2 rounded-xl">
                <Compass className="h-5 w-5" />
              </div>
              <h3 className="font-bold text-lg">资源导航</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              全场景资源聚合，一站直达
            </p>
          </Link>

          <Link
            to="/category/growth"
            className="group relative overflow-hidden rounded-3xl border bg-gradient-to-br from-green-500/10 to-emerald-500/10 p-6 hover:shadow-lg transition-all hover:border-primary/50"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-green-500 text-white p-2 rounded-xl">
                <TrendingUp className="h-5 w-5" />
              </div>
              <h3 className="font-bold text-lg">运营涨粉</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              流量算法、爆款复盘、涨粉技巧
            </p>
          </Link>

          <div
            className="group relative overflow-hidden rounded-3xl border bg-gradient-to-br from-orange-500/10 to-amber-500/10 p-6 hover:shadow-lg transition-all hover:border-primary/50 cursor-pointer"
            onClick={() => {
              const event = new CustomEvent('open-ai-assistant');
              window.dispatchEvent(event);
            }}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-orange-500 text-white p-2 rounded-xl">
                <Sparkles className="h-5 w-5" />
              </div>
              <h3 className="font-bold text-lg">AI 助手</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              智能问答、资源推荐、学习规划
            </p>
          </div>
        </section>

        {/* Categories Section */}
        {categoryData.map((category) => (
          <section key={category.id} className="mt-12 mb-16">
            <div className="flex items-center justify-between mb-8">
              <div className="flex flex-col gap-1">
                <h2 className="text-2xl font-bold tracking-tight">{category.name}</h2>
                <div className="h-1 w-12 bg-primary rounded-full" />
              </div>
              <Button variant="ghost" size="sm" className="text-primary hover:bg-primary/10" asChild>
                <Link to={`/category/${category.id}`}>
                  查看更多 <ChevronRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {category.items.map((tutorial) => (
                <TutorialCard
                  key={tutorial.id}
                  tutorial={tutorial}
                  isFavorited={favorites.includes(tutorial.id)}
                  onFavoriteToggle={toggleFavorite}
                  onClick={addHistory}
                />
              ))}
            </div>
            
            {category.items.length === 0 && (
              <div className="py-12 flex items-center justify-center border rounded-xl bg-muted/20">
                <p className="text-muted-foreground text-sm">此分类下暂无内容</p>
              </div>
            )}
          </section>
        ))}

        {/* Stats Section */}
        <section className="mt-16 mb-12 bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-3xl p-8 md:p-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-extrabold text-primary mb-2">{tutorials.length}+</div>
              <div className="text-sm text-muted-foreground">优质资源</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-extrabold text-primary mb-2">{categories.length}</div>
              <div className="text-sm text-muted-foreground">精选分类</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-extrabold text-primary mb-2">24h</div>
              <div className="text-sm text-muted-foreground">持续更新</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-extrabold text-primary mb-2">100%</div>
              <div className="text-sm text-muted-foreground">免费使用</div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
